import React, { useState, memo, useEffect } from 'react';
import { Box } from '@mui/material';
import { TableCellVariant } from './types';

interface StringInputCellProps {
  value: string | number | null;
  row: number;
  col: number;
  onChange: (value: number | string | null, row: number, col: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>, row: number, col: number) => void;
  variant: TableCellVariant;
  isSelected?: boolean;
  isCopied?: boolean;
  isPasted?: boolean;
}

const cellStyles = {
  backgroundColor: 'white',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 600,
  color: '#162936',
};

const StringInputCell = memo(
  ({
    value: initialValue,
    row,
    col,
    onChange,
    onFocus,
    onBlur,
    onPaste,
    isSelected,
    isCopied,
    isPasted,
  }: StringInputCellProps) => {
    const [value, setValue] = useState(initialValue !== null ? String(initialValue) : '');

    useEffect(() => {
      setValue(initialValue !== null ? String(initialValue) : '');
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      if (newValue === '') {
        onChange(null, row, col);
      } else {
        onChange(newValue, row, col);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e, row, col);
      }
    };

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <input
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            textAlign: 'center',
            ...cellStyles,
            ...(isSelected && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
            ...(isCopied && {
              outline: '2px dashed #2F736E',
              outlineOffset: '-3px',
              backgroundColor: '#2F736E2F',
            }),
            ...(isPasted && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
          }}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onPaste={handlePaste}
        />
      </Box>
    );
  },
);

StringInputCell.displayName = 'StringInputCell';

export default StringInputCell;
